import React, { useState, useContext } from "react";
import dayjs from "dayjs";
import {
  Drawer,
  Form,
  Input,
  Select,
  Button,
  Space,

  Upload,
  Typography,
  Divider,
  Card,
  message,
} from "antd";
import { CloseOutlined, PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import TicketServices, {
  ICreateTicket,
  TicketModule,
  TicketPriority,
  TicketIssueType,
} from "../../Services/TicketServices";
import { adminContext } from "../../Common/PageRoute";
import { IPractices, usePractice } from "../../Context/PracticeContext";
import { useUser } from "../../Context/UserContext";

const { Text } = Typography;
const { TextArea } = Input;

interface CreateTicketPanelProps {
  visible: boolean;
  onClose: (refresh?: boolean) => void;
}

const CreateTicketPanel: React.FC<CreateTicketPanelProps> = ({
  visible,
  onClose,
}) => {
  const { openNotification } = useContext(adminContext);
  const {  allPracticeIds  } = usePractice();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [attachments, setAttachments] = useState<any[]>([]);

  const moduleOptions = [
    { label: "AML", value: TicketModule.AML },
    { label: "Billing", value: TicketModule.Billing },
    { label: "Books", value: TicketModule.Books },
    { label: "CRM", value: TicketModule.CRM },
    { label: "Payroll", value: TicketModule.PAYE },
    { label: "SA", value: TicketModule.SA },
    { label: "Accounts", value: TicketModule.Accounts },
  ];

  const priorityOptions = [
    { value: TicketPriority.P1, label: 'Urgent' },
    { value: TicketPriority.P2, label: 'High' },
    { value: TicketPriority.P3, label: 'Medium' },
    { value: TicketPriority.P4, label: 'Low' },
  ];

  const typeOptions = [
    { label: "Bug", value: TicketIssueType.Bug },
    { label: "Development", value: TicketIssueType.New },
    { label: "Enhancement", value: TicketIssueType.Enhancement },
  ];

  const handleCreateTicket = async (values: any) => {
    setLoading(true);
    try {
      const ticketData: ICreateTicket = {
        title: values.title,
        message: values.content || "",
        phone: values.phone,
        practiceId: values.practiceId,
        module: values.module,
        priority: values.priority,
        issueType: values.issueType,
        attachments: attachments,
      };

      // Call API to create ticket
      const res = await TicketServices.create(ticketData);
      if (res?.status) {
        openNotification("success", "Ticket created successfully");
        form.resetFields();
        setAttachments([]);
        onClose(true);
      } else {
        openNotification("error", res?.message || "Failed to create ticket");
      }
    } catch (error) {
      openNotification("error", "An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };

  const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25 MB

  const handleUpload = (file: any) => {
    if (file.size > MAX_FILE_SIZE) {
      message.error(`Attachment "${file.name}" exceeds the maximum allowed size of 25 MB.`);
      return Upload.LIST_IGNORE;
    }
    setAttachments((prev) => {
      if (prev.length >= 5) {
        message.info("Maximum 5 attachments allowed.");
        return prev;
      }
      return [...prev, { name: file.name, file }];
    });
    return false;
  };

  const handleRemoveAttachment = (index: number) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const clipboardData = e.clipboardData;
    if (!clipboardData) return;

    const file =
      Array.from(clipboardData.files || []).find((f) => f.type?.startsWith("image/")) ||
      Array.from(clipboardData.items || []).find((item) => item.type?.startsWith("image/"))?.getAsFile();

    if (file) {
      e.preventDefault();
      const ext = file.type?.includes("jpeg") || file.type?.includes("jpg") ? "jpg" : "png";
      const fileName = file.name && !/^image(\.(png|jpe?g|gif|webp))?$/i.test(file.name)
        ? file.name
        : `screenshot_${dayjs().format("YYYYMMDD_HHmmss")}.${ext}`;
      const fileObj = new File([file], fileName, { type: file.type || "image/png" });
      handleUpload(fileObj);
    }
  };

  const handleClose = () => {
    form.resetFields();
    setAttachments([]);
    onClose();
  };

  return (
    <Drawer
      title="Create ticket"
      placement="right"
      width={600}
      onClose={handleClose}
      open={visible}
      destroyOnClose
      closable={true}
      extra={
        <Button
          type="text"
          icon={<CloseOutlined />}
          onClick={handleClose}
        />
      }
      footer={
        <div style={{ textAlign: "right" }}>
          <Space>
            <Button onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={() => form.submit()}
              loading={loading}
              style={{ borderRadius: 6 }}
            >
              Save
            </Button>
          </Space>
        </div>
      }
    >
      <div style={{ padding: "8px 16px" }}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateTicket}
          autoComplete="off"
        >
          {/* Title Field */}
          <Form.Item
            label="Title"
            name="title"
            rules={[
              { required: true, message: "Please enter ticket title" },
              { max: 250, message: "Title cannot exceed 250 characters" },
            ]}
            style={{ marginBottom: 16 }}
          >
            <Input
              placeholder="Enter ticket title"
              maxLength={250}
              showCount
              size="middle"
              style={{ borderRadius: 6 }}
            />
          </Form.Item>



          {/* Phone Number */}
          <Form.Item label="Phone number" name="phone" style={{ marginBottom: 16 }}>
            <Input
              placeholder="Client phone number"
              type="tel"
              maxLength={13}
              style={{ borderRadius: 6 }}
            />
          </Form.Item>

          {/* Practice Name */}
          <Form.Item
            label="Practice name"
            name="practiceId"
            rules={[{ required: true, message: "Please select a practice" }]}
            style={{ marginBottom: 16 }}
          >
            <Select
              placeholder="Select practice"
              options={allPracticeIds}
              style={{ borderRadius: 6 }}
            />
          </Form.Item>

          {/* Module */}
          <Form.Item
            label="Module"
            name="module"
            rules={[{ required: true, message: "Please select module" }]}
            style={{ marginBottom: 16 }}
          >
            <Select
              placeholder="Select module"
              options={moduleOptions}
              style={{ borderRadius: 6 }}
            />
          </Form.Item>
          {/* Module */}
          {/* <Form.Item
            label="Module"
            name="module"
            rules={[{ required: true, message: "Please select module" }]}
            style={{ marginBottom: 16 }}
          >
            <Select
              placeholder="Select module"
              options={moduleOptions}
              style={{ borderRadius: 6 }}
            />
          </Form.Item> */}

          {/* Type */}
          <Form.Item
            label="Type"
            name="issueType"
            rules={[{ required: true, message: "Please select type" }]}
            style={{ marginBottom: 16 }}
          >
            <Select
              placeholder="Select type"
              options={typeOptions}
              style={{ borderRadius: 6 }}
            />
          </Form.Item>

          {/* Priority */}
          <Form.Item
            label="Priority"
            name="priority"
            rules={[{ required: true, message: "Please select priority" }]}
            style={{ marginBottom: 16 }}
          >
            <Select
              placeholder="Select priority"
              options={priorityOptions}
              style={{ borderRadius: 6 }}
            />
          </Form.Item>

          <Divider style={{ margin: "16px 0" }} />

          {/* Content Field */}
          <Form.Item
            label="Content"
            name="content"
            rules={[{ required: true, message: "Please enter ticket content" }]}
            style={{ marginBottom: 0 }}
          >
            <TextArea
              rows={8}
              placeholder="Enter ticket description and details..."
              style={{ borderRadius: 6, resize: "vertical" }}
              onPaste={handlePaste}
            />
          </Form.Item>

          <Divider style={{ margin: "24px 0" }} />

          {/* Attachments */}
          <div style={{ paddingTop: 8 }}>
            <Text strong style={{ marginBottom: 12, display: "block", fontSize: 14 }}>
              Attachments
            </Text>

            {attachments.length > 0 && (
              <Card
                size="small"
                style={{
                  marginBottom: 16,
                  paddingBottom: 12,
                  backgroundColor: "#fafafa",
                  border: "1px solid #f0f0f0",
                }}
              >
                <Space direction="vertical" style={{ width: "100%" }} size={8}>
                  {attachments.map((attachment, index) => (
                    <div
                      key={index}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "8px 12px",
                        backgroundColor: "#fff",
                        borderRadius: 4,
                        border: "1px solid #e8e8e8",
                      }}
                    >
                      <Text ellipsis style={{ flex: 1 }}>
                        {attachment.name}
                      </Text>
                      <Button
                        type="text"
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={() => handleRemoveAttachment(index)}
                      />
                    </div>
                  ))}
                </Space>
              </Card>
            )}

            <Upload
              maxCount={5}
              beforeUpload={handleUpload}
              accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.txt"
              style={{ marginBottom: 8 }}
            >
              <Button
                icon={<PlusOutlined />}
                style={{ borderRadius: 6 }}
              >
                Select or drop files
              </Button>
            </Upload>
            <Text type="secondary" style={{ fontSize: 12, marginTop: 12, display: "block", lineHeight: 1.6 }}>
              Max 5 files. Max size 25 MB per file. Accepted: PDF, DOC, XLS, JPG, PNG, TXT
            </Text>
          </div>
        </Form>
      </div>
    </Drawer>
  );
};

export default CreateTicketPanel;